import React from "react";
import { useMapCanvasStage } from "../contexts/MapCanvasContext";
import { EditorCanvas } from "./EditorCanvas";
import { EditorLayout } from "./EditorLayout/EditorLayout";
import { PanelLayoutProvider } from "./EditorLayout/EditorLayoutContext";
import { useGlobalEditorShortcuts } from "./shortcuts/hooks/useGlobalEditorShortcuts";
import { useShapeSelectionShortcuts } from "./shortcuts/hooks/useShapeSelectionShortcuts";
import { EditorToolbars } from "./toolbars/EditorToolbars";
import { EditorZoomPill } from "./toolbars/EditorZoomPill";
import { TopToolbar } from "./toolbars/TopToolbar";

// Isolate shortcut hooks so their context subscriptions don't cause
// the entire MapEditor tree to re-render on every selection change.
const EditorShortcuts: React.FC = () => {
  useGlobalEditorShortcuts();
  useShapeSelectionShortcuts();
  return null;
};

export const MapEditor: React.FC = () => {
  const { centerStage, stagePositionRef } = useMapCanvasStage();
  const lastAutoCenterRef = React.useRef<{
    x: number;
    y: number;
    scale: number;
  } | null>(null);

  // Open centered and fitted to the viewport. The map value (and with it
  // the editing-zone size) loads after mount, so this re-fires whenever
  // centerStage's inputs change — but only while the stage is still at
  // the untouched default or exactly where auto-centering left it. As
  // soon as the position diverges (user pan/zoom, or a host-provided
  // initial position), it backs off for good.
  React.useEffect(() => {
    const { x, y, scale } = stagePositionRef.current;
    const auto = lastAutoCenterRef.current;
    const isDefault = x === 0 && y === 0 && scale === 1;
    const isAutoSet =
      !!auto && x === auto.x && y === auto.y && scale === auto.scale;
    if (!isDefault && !isAutoSet) return;
    // Wait a frame so the Konva stage has mounted and registered.
    const frame = requestAnimationFrame(() => {
      lastAutoCenterRef.current = centerStage() ?? null;
    });
    return () => cancelAnimationFrame(frame);
  }, [centerStage, stagePositionRef]);

  return (
    <PanelLayoutProvider>
      <EditorShortcuts />
      {/* Framed like the settings pages: card with rounded corners on a
          muted background */}
      <div className="h-full w-full bg-v2-background p-v2-layout-card-out">
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-v2-card shadow-componentCard">
          <div className="flex h-12 w-full flex-none">
            <TopToolbar />
          </div>
          <div className="min-h-0 w-full flex-1">
            <EditorLayout>
              <EditorCanvas />
              <EditorZoomPill />
            </EditorLayout>
          </div>
        </div>
      </div>
      <EditorToolbars />
    </PanelLayoutProvider>
  );
};
